import { translate } from "ui/i18n";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { useWindowWidth } from "ui/utils/hooks";
import { useForm } from "react-hook-form";
import { Inputs } from "ui/pages/inputsType";
import { HiPaperAirplane } from "react-icons/hi";
import { INInput } from "ui/pages/app/components/inputs/INInput";
import { INTextarea } from "ui/pages/app/components/inputs/INTextarea";
import { INCheckbox } from "ui/pages/app/components/inputs/INCheckbox";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { selectIsAuthenticationLoading } from "domain/auth/auth.selectors";
import { sendContactEmail } from "domain/auth/auth.actions";
import { Snackbar } from "ui/pages/snackbar";

export function QuoteRequest() {
  const isDesktop = useWindowWidth();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsAuthenticationLoading);
  const email = location.state?.email;
  const { register, handleSubmit, formState, control } = useForm<Inputs>({
    mode: "onSubmit",
    defaultValues: {
      lastName: "",
      firstName: "",
      email: email || "",
    },
  });
  const { errors, isDirty } = formState;

  const handleFormSubmit = async (data: Inputs) => {
    const result = await dispatch(
      sendContactEmail({
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        company: data.company,
        need: data.need,
        type: "quoteRequest",
      })
    ).unwrap();
    if (result.success) {
      Snackbar.showSuccess({
        title: translate("ns1:QuoteRequest.RequestSent"),
      });
      navigate("/choice-confirmation", {
        state: { email, quoteSent: true },
      });
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="text-center">
        {translate("ns1:QuoteRequest.QuoteRequest")}
      </h2>

      <div className="max-w-[530px]">
        <h4 className="mt-2 text-center text-sm md:mt-4 md:text-lg">
          {translate("ns1:QuoteRequest.Subtitle1")}
        </h4>
        <h4 className="mt-2 text-center text-sm md:mt-2 md:text-lg">
          {translate("ns1:QuoteRequest.Subtitle2")}
        </h4>
      </div>

      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="mt-4 w-full max-w-[530px] space-y-2 md:mt-12 md:space-y-4"
      >
        <INInput
          error={errors.lastName}
          label={translate("ns1:Input.Labels.Name") + "*"}
          placeholder={translate("ns1:Input.Labels.LastName")}
          register={{
            ...register("lastName", {
              required: translate("ns1:Input.Errors.Required"),
            }),
          }}
        />
        <INInput
          error={errors.firstName}
          label={translate("ns1:Input.Labels.FirstName") + "*"}
          placeholder={translate("ns1:Input.Labels.FirstName")}
          register={{
            ...register("firstName", {
              required: translate("ns1:Input.Errors.Required"),
            }),
          }}
        />
        <INInput
          error={errors.company}
          label={translate("ns1:QuoteRequest.CompanyName") + "*"}
          placeholder={translate("ns1:QuoteRequest.Company")}
          register={{
            ...register("company", {
              required: translate("ns1:Input.Errors.Required"),
            }),
          }}
        />
        <INInput
          disabled={!!email}
          error={errors.email}
          label={translate("ns1:Input.Labels.Email") + "*"}
          placeholder="mail@exemple.com"
          register={{
            ...register("email", {
              required: translate("ns1:Input.Errors.Required"),
              pattern: {
                value: /\S+@\S+\.\S+/,
                message: translate("ns1:Input.Errors.EmailFormat"),
              },
            }),
          }}
        />
        <INTextarea
          error={errors.need}
          label={translate("ns1:QuoteRequest.YourNeed") + "*"}
          placeholder={translate("ns1:QuoteRequest.YourNeedPlaceholder")}
          register={{
            ...register("need", {
              required: translate("ns1:Input.Errors.Required"),
            }),
          }}
          rows={isDesktop ? 4 : 4}
        />
        <INCheckbox
          inputName="consent"
          control={control}
          label={translate("ns1:QuoteRequest.DataConsent") + "*"}
          text={translate("ns1:QuoteRequest.DataConsentText")}
          required
        />
        <div className="!mt-6 text-center text-xs leading-5 text-darkGrey-600 md:!mt-10">
          {translate("ns1:QuoteRequest.FillAllFields")}
        </div>

        <PrimaryButton
          disabled={!isDirty || isLoading}
          type="submit"
          height={isDesktop ? "56px" : "46px"}
          width="100%"
          icon={<HiPaperAirplane size={isDesktop ? 24 : 20} />}
          className="w-full"
        >
          {translate("ns1:Button.Send")}
        </PrimaryButton>
      </form>
    </div>
  );
}
